import is from '@sindresorhus/is';
import { noop } from 'lodash';
import type { FunctionComponent } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { SkeletonBase } from '~/ui/core/skeleton/Skeleton';
import { TileStickyHeader } from '~/ui/core/tile/Tile';
import { SidepanelHeader } from '~/ui/domain/Sidepanel/components/SidepanelHeader';

import { SectionContent } from '../../comments/section-content';
import { useTileNavigation } from '../../url-tiles';
import { Action as ActionWrapper } from '../components/Action';
import { ContentContainer, ContentSections, Section, SectionHeader } from '../components/Common';
import {
    CategoryListSkeleton,
    DescriptionSectionSkeleton,
    SkeletonBreadcrumbs,
    TitleSkeleton,
} from '../components/SkeletonLoaders';
import { AuditTableSkeleton } from './AuditTable';

export const LoadingContent: FunctionComponent<{ readonly onClose?: () => void; readonly tileKey?: string }> = ({
    onClose,
    tileKey,
}) => {
    const translate = useTranslate();
    const tileNavigation = useTileNavigation(tileKey);

    const stickyHeader = (
        <TileStickyHeader compact={true}>
            <SidepanelHeader
                actions={undefined}
                activeLanguage={undefined}
                disableArrows={is.undefined(tileNavigation)}
                isGenerating={false}
                isLanguagePickerDisabled={true}
                languages={[]}
                setActiveLanguage={noop}
                onClose={onClose}
                onNext={tileNavigation?.onNext}
                onPrevious={tileNavigation?.onPrevious}
            />
        </TileStickyHeader>
    );

    return (
        <div className="flex w-full min-w-0 flex-col gap-0" data-testid="loading-content">
            {stickyHeader}
            <div className="flex min-w-0 flex-col gap-s">
                <SkeletonBreadcrumbs />
                <TitleSkeleton />
            </div>
            <ContentContainer>
                <div className="mt-12xl flex flex-col gap-xxs overflow-hidden rounded-xl bg-surface-hover p-xxs shadow-sm">
                    <ActionWrapper $surface="integrated">
                        <div className="flex min-w-0 items-start">
                            <SkeletonBase className="h-[32px] w-full max-w-[398px] rounded-lg" />
                        </div>
                        <div className="flex min-w-0 justify-end">
                            <SkeletonBase className="h-[36px] w-[131px] rounded-lg" />
                        </div>
                    </ActionWrapper>
                    <AuditTableSkeleton />
                </div>
                <div style={{ display: 'flex', flexDirection: 'row', gap: '16px', marginTop: '2.1rem' }}>
                    <ContentSections>
                        <Section>
                            <SectionHeader>{translate('pages.job-architecture-v2.job-profile-title')}</SectionHeader>
                            <DescriptionSectionSkeleton />
                            <DescriptionSectionSkeleton />
                            <DescriptionSectionSkeleton />
                        </Section>
                        <Section>
                            <SectionHeader>
                                {translate('pages.job-architecture-v2.job-profile-required-skills')}
                            </SectionHeader>
                            <SectionContent>
                                <CategoryListSkeleton />
                            </SectionContent>
                        </Section>
                    </ContentSections>
                </div>
            </ContentContainer>
        </div>
    );
};
